import React, {useCallback, useEffect, useRef, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Alert, Button, Spinner} from 'reactstrap';
import CodeBlock from '../shared/CodeBlock.tsx';
import Icon from '../shared/Icon.tsx';
import {CopyIcon, RotateCwIcon, TriangleAlertIcon} from 'lucide-react';
import useAiHub from '../assistant/useAiHub.ts';
import {AiChunk} from '../../api/types.ts';
import ReactDOMServer from 'react-dom/server';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import HtmlPageCodeBlock from './HtmlPageCodeBlock.tsx';

interface VibeCodingOutputProps {
    id: string;
    onHtmlReady?: (html: string | null) => void;
    // Reports a human-readable problem (stall, connection drop, empty result)
    // to the parent, or null while everything is healthy.
    onError?: (message: string | null) => void;
    // Re-runs generation from the same prompt. When provided, a "Try again"
    // button is shown alongside any error.
    onRetry?: () => void;
    // Once the AI says it has finished (the stream's completion signal).
    onComplete?: () => void;
}

// How often we re-check for a stalled stream.
const STALL_CHECK_INTERVAL_MS = 5000;
// The background job can take a while to produce its first token (queueing +
// a reasoning model), so we allow longer before the first chunk than between
// subsequent chunks.
const STALL_BEFORE_FIRST_CHUNK_MS = 90000;
const STALL_AFTER_CHUNK_MS = 45000;

const STALL_MESSAGE =
    'Generation seems to have stalled — we haven’t heard back from the AI ' +
    'for a while, so it may have failed. Please close and try generating ' +
    'again.';
const EMPTY_RESULT_MESSAGE =
    'The AI finished without returning any code. It may have hit an error — ' +
    'please close and try generating again.';

// True when the output clearly is a bare HTML document (starts with a doctype
// or <html> tag) rather than Markdown prose. When that's the case we render it
// in a syntax-highlighted code block instead of letting ReactMarkdown dump the
// raw tags as text — and we do it while streaming, before </html> arrives.
const looksLikeBareHtmlDocument = (output: string): boolean => {
    const hasHtmlFence = /```(?:html|htm)\s*/i.test(output);
    if (hasHtmlFence) {
        return false;
    }
    const trimmed = output.trimStart();
    return /^<!doctype\s+html\b/i.test(trimmed) || /^<html\b/i.test(trimmed);
};

// Pulls the last complete HTML document from either a fenced Markdown block or
// a raw HTML response. A closing </html> tag is required so partially streamed
// documents cannot be saved.
const extractCompleteHtml = (output: string): string | null => {
    const fenceMatches = Array.from(output.matchAll(/```(?:html|htm)\s*/gi));
    const lastFence = fenceMatches[fenceMatches.length - 1];

    if (lastFence?.index !== undefined) {
        const afterFence = output.slice(lastFence.index + lastFence[0].length);
        const fenceEnd = afterFence.indexOf('```');
        const html = (
            fenceEnd === -1 ? afterFence : afterFence.slice(0, fenceEnd)
        ).trim();

        return /<\/html\s*>/i.test(html) ? html : null;
    }

    const htmlStart = output.search(/<html\b/i);
    if (htmlStart === -1) {
        return null;
    }

    const doctypeStart = output.search(/<!doctype\s+html\b/i);
    const documentStart =
        doctypeStart !== -1 && doctypeStart < htmlStart
            ? doctypeStart
            : htmlStart;
    const document = output.slice(documentStart);
    const closingTag = /<\/html\s*>/i.exec(document);

    return closingTag
        ? document.slice(0, closingTag.index + closingTag[0].length).trim()
        : null;
};

const VibeCodingOutput: React.FC<VibeCodingOutputProps> = ({
    id,
    onHtmlReady,
    onError,
    onRetry,
    onComplete: onFinished
}) => {
    const {loggedInUserId} = useAuthStateManager();
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        if (isComplete) {
            onFinished?.();
        }
        // Only the moment it finishes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComplete]);
    const [hasStalled, setHasStalled] = useState(false);

    const [output, setOutput] = useState('');
    const completeHtml = extractCompleteHtml(output);
    const isRawHtml = looksLikeBareHtmlDocument(output);

    // Timestamp of the last streamed chunk (or mount), used to detect a stall.
    const lastActivityRef = useRef<number>(Date.now());
    const hasReceivedChunkRef = useRef(false);

    // Generation is finished once we see a completion signal or a full HTML
    // document — the stream's completion signal isn't always reliable.
    const generationFinished = isComplete || completeHtml !== null;

    const [copyState, setCopyState] = useState<{
        copied: boolean;
        text: string;
        className: string;
    }>({
        copied: false,
        text: 'Copy',
        className: ''
    });

    const groupName = `VibeCode_${id}_${loggedInUserId}`;

    const onChunkGenerated = useCallback((aiChunk: AiChunk) => {
        lastActivityRef.current = Date.now();
        hasReceivedChunkRef.current = true;
        // Any fresh activity clears a previously-detected stall.
        setHasStalled(false);
        setOutput(prevState => prevState + aiChunk.chunk);
    }, []);

    const onComplete = useCallback(() => {
        setIsComplete(true);
    }, [setIsComplete]);

    const {connectionError} = useAiHub({
        groupName,
        onChunkGenerated,
        onComplete
    });

    // Watch for a stalled stream: no chunks for longer than the threshold while
    // generation is still supposedly running. This covers the "still says
    // Generating but nothing is happening" case where OnComplete never arrives
    // (hung background job or dropped connection).
    useEffect(() => {
        if (generationFinished) {
            return;
        }
        const interval = window.setInterval(() => {
            const idleMs = Date.now() - lastActivityRef.current;
            const threshold = hasReceivedChunkRef.current
                ? STALL_AFTER_CHUNK_MS
                : STALL_BEFORE_FIRST_CHUNK_MS;
            if (idleMs > threshold) {
                setHasStalled(true);
            }
        }, STALL_CHECK_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [generationFinished]);

    // Surface any condition that means the user is waiting on nothing.
    const finishedButEmpty = isComplete && output.trim() === '';
    const problemMessage =
        connectionError ??
        (finishedButEmpty
            ? EMPTY_RESULT_MESSAGE
            : hasStalled
            ? STALL_MESSAGE
            : null);

    // Report the finished HTML to the parent so it can enable "Save and close".
    // Driven by the streamed content (closing </html>), not the completion
    // signal, so it matches the in-block Save/Download availability.
    useEffect(() => {
        onHtmlReady?.(completeHtml);
    }, [completeHtml, onHtmlReady]);

    // Keep the parent informed of any problem so it can adjust its own status
    // messaging (e.g. stop showing "Generating…").
    useEffect(() => {
        onError?.(problemMessage);
    }, [problemMessage, onError]);

    const renderMarkdown = useCallback(
        (
            markdownContent: string,
            context: 'display' | 'clipboard' = 'display'
        ) => (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    code({node, className, children, ...props}) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : null;
                        if (context === 'clipboard') {
                            return <code>{children}</code>;
                        }

                        if (language === 'html') {
                            return (
                                <HtmlPageCodeBlock
                                    value={String(children).replace(/\n$/, '')}
                                    {...props}
                                />
                            );
                        }

                        return language ? (
                            <CodeBlock
                                language={language}
                                value={String(children).replace(/\n$/, '')}
                                {...props}
                            />
                        ) : (
                            <code className={className} {...props}>
                                {children}
                            </code>
                        );
                    },
                    a({href, children, ...props}) {
                        const isExternal =
                            href &&
                            !href.startsWith('/') &&
                            !href.startsWith('#');
                        if (context === 'clipboard') {
                            return `${children} (${href})`;
                        }
                        return (
                            <a
                                href={href}
                                target={isExternal ? '_blank' : undefined}
                                rel={
                                    isExternal
                                        ? 'noopener noreferrer'
                                        : undefined
                                }
                                {...props}
                            >
                                {children}
                            </a>
                        );
                    }
                }}
            >
                {markdownContent}
            </ReactMarkdown>
        ),
        []
    );

    const copyToClipboard = useCallback(() => {
        const markdownContent = output;
        const htmlContent = ReactDOMServer.renderToString(
            renderMarkdown(markdownContent, 'clipboard')
        );

        if (navigator.clipboard && window.ClipboardItem) {
            const clipboardItem = new ClipboardItem({
                'text/plain': new Blob([markdownContent], {type: 'text/plain'}),
                'text/html': new Blob([htmlContent], {type: 'text/html'})
            });

            navigator.clipboard
                .write([clipboardItem])
                .then(() => {
                    setCopyState({
                        copied: true,
                        text: 'Copied!',
                        className: 'text-success'
                    });
                })
                .catch(err => {
                    console.error('Failed to copy: ', err);
                    setCopyState({
                        copied: false,
                        text: 'Failed to copy',
                        className: 'text-danger'
                    });
                });
        } else {
            // Fallback for browsers that don't support the Clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = markdownContent;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyState({
                    copied: true,
                    text: 'Copied!',
                    className: 'text-success'
                });
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
                setCopyState({
                    copied: false,
                    text: 'Failed to copy',
                    className: 'text-danger'
                });
            }
            document.body.removeChild(textArea);
        }
    }, [output, renderMarkdown]);

    useEffect(() => {
        let timer: number;
        if (copyState.copied) {
            timer = setTimeout(() => {
                setCopyState({copied: false, text: 'Copy', className: ''});
            }, 2000);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [copyState.copied]);

    // Stop the spinner once finished or once we've flagged a problem, so we
    // never keep spinning against a stalled/failed stream.
    const showSpinner = !generationFinished && !problemMessage;

    return (
        <div>
            {showSpinner && (
                <Spinner
                    type="grow"
                    size="sm"
                    color="primary"
                    className="mb-2"
                />
            )}
            {problemMessage && (
                <Alert color="danger" className="mb-2">
                    <div className="d-flex align-items-start">
                        <Icon icon={TriangleAlertIcon} className="me-2 mt-1" />
                        <div className="flex-grow-1">
                            <div>{problemMessage}</div>
                            {onRetry && (
                                <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    className="mt-2"
                                    onClick={onRetry}
                                >
                                    <Icon
                                        icon={RotateCwIcon}
                                        className="me-2"
                                    />
                                    Try again
                                </Button>
                            )}
                        </div>
                    </div>
                </Alert>
            )}
            {isRawHtml ? (
                <HtmlPageCodeBlock value={completeHtml ?? output.trimStart()} />
            ) : (
                renderMarkdown(output, 'display')
            )}
            {output !== '' && generationFinished && (
                <span className="folder-action-icons">
                    <button
                        onClick={copyToClipboard}
                        className={`btn btn-link p-0 mr-2 ${copyState.className}`}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            textDecoration: 'none'
                        }}
                    >
                        {copyState.text} <Icon icon={CopyIcon} />
                    </button>
                </span>
            )}
        </div>
    );
};

export default VibeCodingOutput;
